import { Card } from '@tempo-io/sui-card';
import { cn } from '@tempo-io/sui-class-utils';
import { forwardRef } from 'react';
import { bindCardSelection, useIsCardSelected } from '@/common/components/ui/swimlane/context';
import { useContextMenu } from '@/common/components/ui/swimlane/context-menu';
import type { ICardProps } from '@/common/components/ui/swimlane/types';
import { buildCardId } from '@/common/components/ui/swimlane/utils';
import { CARD_WIDTH, COMPACT_CARD_WIDTH, DEFAULT_COLOR } from '../../constants';
import {
  cardClickDisabledCssSelector,
  openCardDetailsCssSelector,
} from '../../hooks/useCardEventHandlers';

type StyledCardProps = React.ComponentProps<'div'> & {
  isSelected?: boolean;
  borderColor?: string;
  isMenuOpen?: boolean;
  isCompact?: boolean;
  children?: React.ReactNode;
};

export const StyledCard = forwardRef<HTMLDivElement, StyledCardProps>(
  (
    {
      isSelected,
      borderColor = DEFAULT_COLOR,
      isMenuOpen,
      isCompact,
      className,
      style,
      children,
      ...props
    },
    ref,
  ) => {
    return (
      <Card
        ref={ref}
        className={cn(
          'relative rounded-xl border-0 shadow-xs transition-shadow hover:shadow-md hover:z-5',
          isCompact ? 'p-2.5 gap-1.5' : 'p-3 gap-2',
          `${openCardDetailsCssSelector}:cursor-pointer`,
          `[${openCardDetailsCssSelector}]:cursor-pointer`,
          `${cardClickDisabledCssSelector}:cursor-default`,
          `[${cardClickDisabledCssSelector}]:cursor-default`,
          className,
          {
            'shadow-lg': isSelected,
          },
        )}
        style={{
          ...style,
          outline: `1px solid ${borderColor}`,
          borderLeft: `3px solid ${borderColor}`,
          ...(isCompact ? { width: COMPACT_CARD_WIDTH.label } : { width: CARD_WIDTH.label }),
        }}
        {...props}
      >
        {children}
      </Card>
    );
  },
);

type SelectableCardProps = ICardProps &
  Omit<React.ComponentProps<'div'>, 'ref'> & {
    borderColor?: string;
    isCompact?: boolean;
  };

export const SelectableCard = forwardRef<HTMLDivElement, SelectableCardProps>(
  ({ itemID, rowID, columnID, borderColor = DEFAULT_COLOR, isCompact, ...props }, ref) => {
    const isSelected = useIsCardSelected({ itemID, rowID, columnID });
    const { contextMenu } = useContextMenu();
    const cardProps = { itemID, rowID, columnID };

    return (
      <StyledCard
        ref={ref}
        isSelected={isSelected}
        isCompact={isCompact}
        borderColor={borderColor}
        isMenuOpen={contextMenu.open && contextMenu.itemID === buildCardId(cardProps)}
        {...bindCardSelection(cardProps)}
        {...props}
      />
    );
  },
);
